import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfDay,
  endOfDay,
  addDays,
  addWeeks,
  subWeeks,
  isSameDay,
  parseISO,
} from "date-fns";
import { getShifts, getProjects, getVenues, extractErrorMessage } from "../lib/api";
import { LoadingSpinner } from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { 
  useCalendarEdit, 
  CalendarEditControls, 
  CalendarEditModeBanner,
  CalendarEditLegend,
  EditableShiftBlock,
  ShiftCreateModal,
  GhostShiftPreview,
} from "../components/calendar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
  Users,
  Clock,
  AlertTriangle,
  Moon,
  Plus,
} from "lucide-react";
import { cn } from "../lib/utils";

// Generate hours for the timeline (6am to 10pm)
const HOURS = Array.from({ length: 17 }, (_, i) => i + 6);
const HOUR_HEIGHT = 60; // pixels per hour

// Color palette for projects
const PROJECT_COLORS = [
  { bg: "bg-teal-500/20", border: "border-teal-500", text: "text-teal-400" },
  { bg: "bg-blue-500/20", border: "border-blue-500", text: "text-blue-400" },
  { bg: "bg-purple-500/20", border: "border-purple-500", text: "text-purple-400" },
  { bg: "bg-amber-500/20", border: "border-amber-500", text: "text-amber-400" },
  { bg: "bg-rose-500/20", border: "border-rose-500", text: "text-rose-400" },
  { bg: "bg-emerald-500/20", border: "border-emerald-500", text: "text-emerald-400" },
  { bg: "bg-cyan-500/20", border: "border-cyan-500", text: "text-cyan-400" },
  { bg: "bg-orange-500/20", border: "border-orange-500", text: "text-orange-400" },
];

export default function ProjectCalendarPage() {
  const navigate = useNavigate();
  const { formatDate, formatTime, formatDuration } = useFormatters();
  const { 
    isEditMode, 
    clearSelection, 
    registerOnSaveSuccess, 
    getPendingChange,
    startShiftCreation,
    updateShiftCreation,
    endShiftCreation,
    isCreatingShift,
    openCreateModal,
  } = useCalendarEdit();
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [venues, setVenues] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("week"); // "day" | "week"
  const [selectedShift, setSelectedShift] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Ref for tracking calendar column containers
  const calendarColumnRefs = useRef(new Map());

  // Load data function - reusable for refresh
  const loadData = async () => {
    try {
      const [shiftsRes, projectsRes, venuesRes] = await Promise.all([
        getShifts({ limit: 500 }),
        getProjects({ limit: 100 }),
        getVenues({ limit: 100 }),
      ]);
      setShifts(shiftsRes.data || []);
      setProjects(projectsRes.data || []);
      setVenues(venuesRes.data || []);
    } catch (err) {
      console.error("Failed to load calendar data:", extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadData();
  }, []);

  // Register refresh callback for save success
  useEffect(() => {
    registerOnSaveSuccess(loadData);
  }, [registerOnSaveSuccess]);

  // Create project color map
  const projectColorMap = useMemo(() => {
    const map = new Map();
    projects.forEach((project, index) => {
      map.set(project.id, PROJECT_COLORS[index % PROJECT_COLORS.length]);
    });
    return map;
  }, [projects]);

  // Get venue name
  const getVenueName = (venueId) => {
    const venue = venues.find((v) => v.id === venueId);
    return venue?.name || "Unknown";
  };

  // Get project name
  const getProjectName = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    return project?.name || "Unknown Project";
  };

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === "day") {
      return {
        start: startOfDay(currentDate),
        end: endOfDay(currentDate),
        days: [currentDate],
      };
    } else {
      const start = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
      const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
      return {
        start,
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
        days,
      };
    }
  }, [currentDate, viewMode]);

  // Filter shifts for current date range
  const filteredShifts = useMemo(() => {
    return shifts.filter((shift) => {
      if (!shift.date) return false;
      const shiftDate = parseISO(shift.date);
      return dateRange.days.some((day) => isSameDay(shiftDate, day));
    });
  }, [shifts, dateRange]);

  // Group shifts by day
  const shiftsByDay = useMemo(() => {
    const grouped = new Map();
    dateRange.days.forEach((day) => {
      grouped.set(format(day, "yyyy-MM-dd"), []);
    });
    
    filteredShifts.forEach((shift) => {
      const dayKey = shift.date;
      if (grouped.has(dayKey)) {
        grouped.get(dayKey).push(shift);
      }
    });
    
    return grouped;
  }, [filteredShifts, dateRange]);

  // Navigation handlers
  const goToPrevious = () => {
    if (viewMode === "day") {
      setCurrentDate(addDays(currentDate, -1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === "day") {
      setCurrentDate(addDays(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Calculate shift position and height
  const getShiftStyle = (shift) => {
    const [startHour, startMin] = (shift.start_time || "09:00").split(":").map(Number);
    const [endHour, endMin] = (shift.end_time || "17:00").split(":").map(Number);
    
    // Handle overnight shifts
    let adjustedEndHour = endHour;
    let adjustedEndMin = endMin;
    if (endHour < startHour || (endHour === startHour && endMin < startMin)) {
      adjustedEndHour = 22; // Cap at 10pm
      adjustedEndMin = 0;
    }
    
    const startOffset = (startHour - 6) * HOUR_HEIGHT + (startMin / 60) * HOUR_HEIGHT;
    const durationMinutes = (adjustedEndHour - startHour) * 60 + (adjustedEndMin - startMin);
    const height = Math.max((durationMinutes / 60) * HOUR_HEIGHT, 30); // Min 30px
    
    return {
      top: `${Math.max(startOffset, 0)}px`,
      height: `${height}px`,
    };
  };

  // Check if shift is long (>8 hours)
  const isLongShift = (shift) => {
    return shift.duration_hours && shift.duration_hours > 8;
  };

  // Check if shift is understaffed
  const isUnderstaffed = (shift) => {
    const assigned = shift.assigned_count || 0;
    const required = shift.staff_required || 0;
    return required > assigned;
  };

  // Open shift detail
  const handleShiftClick = (shift) => {
    setSelectedShift(shift);
    setDetailOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-secondary tracking-tight">Project Shifts Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View all shifts organized by project
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* New Shift Button - only visible in edit mode */}
          {isEditMode && (
            <Button
              variant="default"
              size="sm"
              onClick={() => openCreateModal(currentDate)}
              className="bg-primary hover:bg-primary/90"
              data-testid="calendar-new-shift-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              New Shift
            </Button>
          )}
          
          {/* Edit Mode Toggle */}
          <CalendarEditControls />
          
          {/* View Mode Toggle */}
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-28" data-testid="calendar-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Day</SelectItem>
              <SelectItem value="week">Week</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={goToToday} data-testid="calendar-today-btn">
            Today
          </Button>
        </div>
      </div>

      {/* Edit Mode Banner */}
      <CalendarEditModeBanner />

      {/* Navigation */}
      <div className="flex items-center justify-between bg-card/50 rounded-lg p-3 border border-white/10">
        <Button variant="ghost" size="icon" onClick={goToPrevious} data-testid="calendar-prev-btn">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        
        <div className="text-center">
          <h2 className="text-lg font-semibold font-secondary" data-testid="calendar-date-range">
            {viewMode === "day"
              ? format(currentDate, "EEEE, MMMM d, yyyy")
              : `${format(dateRange.start, "MMM d")} - ${format(dateRange.end, "MMM d, yyyy")}`}
          </h2>
        </div>
        
        <Button variant="ghost" size="icon" onClick={goToNext} data-testid="calendar-next-btn">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-muted-foreground">Understaffed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Moon className="h-3.5 w-3.5 text-purple-500" />
          <span className="text-muted-foreground">Long Shift (&gt;8h)</span>
        </div>
        {/* Edit mode legend */}
        <CalendarEditLegend />
      </div>

      {/* Calendar Grid */}
      <div className="bg-card/30 rounded-lg border border-white/10 overflow-hidden">
        {/* Day Headers */}
        <div className={cn(
          "grid border-b border-white/10",
          viewMode === "day" ? "grid-cols-1" : "grid-cols-7"
        )}>
          <div className="w-16 shrink-0" /> {/* Spacer for time column */}
          {dateRange.days.map((day) => (
            <div
              key={format(day, "yyyy-MM-dd")}
              className={cn(
                "py-3 px-2 text-center border-l border-white/10 first:border-l-0",
                isSameDay(day, new Date()) && "bg-primary/10"
              )}
            >
              <div className="text-xs text-muted-foreground uppercase">
                {format(day, "EEE")}
              </div>
              <div className={cn(
                "text-lg font-semibold",
                isSameDay(day, new Date()) && "text-primary"
              )}>
                {format(day, "d")}
              </div>
            </div>
          ))}
        </div>

        {/* Time Grid */}
        <ScrollArea className="h-[600px]">
          <div className="flex">
            {/* Time Labels */}
            <div className="w-16 shrink-0 border-r border-white/10">
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="h-[60px] px-2 py-1 text-xs text-muted-foreground text-right border-b border-white/5"
                >
                  {format(new Date().setHours(hour, 0), "h a")}
                </div>
              ))}
            </div>

            {/* Day Columns */}
            <div className={cn(
              "flex-1 grid",
              viewMode === "day" ? "grid-cols-1" : "grid-cols-7"
            )}>
              {dateRange.days.map((day) => {
                const dayKey = format(day, "yyyy-MM-dd");
                const dayShifts = shiftsByDay.get(dayKey) || [];
                
                // Mouse event handlers for shift creation
                const handleMouseDown = (e) => {
                  if (!isEditMode) return;
                  // Only handle left click on empty area (not on shifts)
                  if (e.button !== 0 || e.target !== e.currentTarget) return;
                  
                  const rect = e.currentTarget.getBoundingClientRect();
                  startShiftCreation(day, e.clientY, rect.top);
                };
                
                const handleMouseMove = (e) => {
                  if (!isCreatingShift) return;
                  updateShiftCreation(e.clientY);
                };
                
                const handleMouseUp = (e) => {
                  if (!isCreatingShift) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  endShiftCreation(rect.top);
                };
                
                return (
                  <div
                    key={dayKey}
                    ref={(el) => calendarColumnRefs.current.set(dayKey, el)}
                    className={cn(
                      "relative border-l border-white/10 first:border-l-0",
                      isEditMode && "cursor-crosshair"
                    )}
                    style={{ height: `${HOURS.length * HOUR_HEIGHT}px` }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={() => isCreatingShift && endShiftCreation(0)}
                    data-testid={`calendar-day-${dayKey}`}
                  >
                    {/* Hour lines */}
                    {HOURS.map((hour) => (
                      <div
                        key={hour}
                        className={cn(
                          "absolute w-full border-b border-white/5",
                          isEditMode && "pointer-events-none"
                        )}
                        style={{ top: `${(hour - 6) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                      />
                    ))}
                    
                    {/* Ghost preview for new shift creation */}
                    {isCreatingShift && (
                      <GhostShiftPreview containerTop={calendarColumnRefs.current.get(dayKey)?.getBoundingClientRect().top || 0} />
                    )}

                    {/* Shifts */}
                    {dayShifts.map((shift) => {
                      const colors = projectColorMap.get(shift.project_id) || PROJECT_COLORS[0];
                      const pendingChange = getPendingChange(shift.id);
                      const displayShift = pendingChange?.modified || shift;
                      const style = getShiftStyle(displayShift);
                      const long = isLongShift(displayShift);
                      const understaffed = isUnderstaffed(displayShift);
                      
                      return (
                        <EditableShiftBlock
                          key={shift.id}
                          shiftId={shift.id}
                          shiftData={shift}
                          className={cn(
                            "absolute left-1 right-1 rounded-md border-l-4 p-1.5 overflow-hidden",
                            colors.bg,
                            colors.border,
                            !isEditMode && "cursor-pointer hover:scale-[1.02] hover:shadow-lg transition-all"
                          )}
                          style={style}
                          onClick={() => handleShiftClick(shift)}
                          colors={colors}
                          data-testid={`shift-block-${shift.id}`}
                        >
                          <div className="flex items-start justify-between gap-1">
                            <div className="min-w-0 flex-1">
                              <p className={cn("text-xs font-semibold truncate", colors.text)}>
                                {getProjectName(shift.project_id)}
                              </p>
                              <p className={cn(
                                "text-[10px] truncate",
                                pendingChange ? "text-amber-400 font-medium" : "text-muted-foreground"
                              )}>
                                {displayShift.start_time} - {displayShift.end_time}
                              </p>
                              {viewMode === "day" && (
                                <>
                                  <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                                    {shift.name}
                                  </p>
                                  <p className="text-[10px] text-muted-foreground truncate">
                                    <MapPin className="inline h-2.5 w-2.5 mr-0.5" />
                                    {getVenueName(shift.venue_id)}
                                  </p>
                                </>
                              )}
                            </div>
                            <div className="flex flex-col gap-0.5">
                              {understaffed && (
                                <AlertTriangle className="h-3 w-3 text-amber-500" />
                              )}
                              {long && (
                                <Moon className="h-3 w-3 text-purple-500" />
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            <Users className="h-2.5 w-2.5 text-muted-foreground" />
                            <span className={cn(
                              "text-[10px]",
                              understaffed ? "text-amber-500" : "text-muted-foreground"
                            )}>
                              {shift.assigned_count || 0}/{shift.staff_required || 0}
                            </span>
                          </div>
                        </EditableShiftBlock>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </div>

      {/* Shift Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-surface-base border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Shift Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedShift && (
            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-white">
                  {selectedShift.name || "Unnamed Shift"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {getProjectName(selectedShift.project_id)}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="text-white font-medium">{formatDate(selectedShift.date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Time</p>
                  <p className="text-white font-medium">
                    {selectedShift.start_time} - {selectedShift.end_time}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Duration</p>
                  <p className="text-white font-medium">
                    {selectedShift.duration_hours?.toFixed(1) || "N/A"} hours
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Crew</p>
                  <p className={cn(
                    "font-medium",
                    isUnderstaffed(selectedShift) ? "text-amber-500" : "text-white"
                  )}>
                    {selectedShift.assigned_count || 0} / {selectedShift.staff_required || 0}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Venue</p>
                  <p className="text-white font-medium">
                    {getVenueName(selectedShift.venue_id)}
                  </p>
                </div>
              </div>

              <div className="flex gap-2">
                {isUnderstaffed(selectedShift) && (
                  <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Understaffed
                  </Badge>
                )}
                {isLongShift(selectedShift) && (
                  <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/30">
                    <Moon className="h-3 w-3 mr-1" />
                    Long Shift
                  </Badge>
                )}
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setDetailOpen(false);
                  navigate(`/projects/${selectedShift.project_id}`);
                }}
                data-testid="view-project-btn"
              >
                View Project
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Shift Create Modal */}
      <ShiftCreateModal />
    </div>
  );
}
